import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { logger } from './utils/logger';
import {
  hasSessionFlag,
  setSessionFlag,
  clearSessionFlag,
  hasStaleCallbackParams,
  stripCallbackParams,
} from './auth/silentReauth';
import { useIdleTimeout } from './hooks/useIdleTimeout';
// Auth
import Login from './pages/auth/Login';
import Profile from './pages/auth/Profile';

// Core
import Home from './pages/Home';
import CreateOrganization from './pages/CreateOrganization';
import Dashboard from './pages/Dashboard';
import Timeline from './pages/Timeline';

// Dashboards
import TreasuryDashboard from './pages/dashboards/TreasuryDashboard';
import SrClerkDashboard from './pages/dashboards/SrClerkDashboard';
import GoverningBodyDashboard from './pages/dashboards/GoverningBodyDashboard';
import JrClerkDashboard from './pages/dashboards/JrClerkDashboard';
import NormalMemberDashboard from './pages/dashboards/NormalMemberDashboard';

// Contribution
import ContributionForm from './pages/contributions/ContributionForm';
import Contributions from './pages/contributions/Contributions';

// Operations
import AddExpensePage from './pages/operations/AddExpensePage';
import ExpensesPage from './pages/operations/ExpensesPage';
import Transactions from './pages/operations/Transactions';
import ApproveExpense from './pages/operations/ApproveExpense';
import DeleteTransaction from './pages/operations/DeleteTransaction';
import ViewExpenses from './pages/operations/ViewExpenses';
import PendingDeleteRequests from './pages/operations/PendingDeleteRequests';
import MyDeletionRequests from './pages/operations/MyDeletionRequests';

// Insights
import Reports from './pages/insights/Reports';
import Notifications from './pages/insights/Notifications';

// Admin
import AdminDashboard from './pages/admin/AdminDashboard';
// NEW: these used to be rendered locally inside AdminDashboard with their
// own `view`/`editingProfile` state. They are now routed through the same
// pageStack as everything else, so Back / Edit Profile work the same way
// they do for Contributions, Timeline, Reports, etc.
// Adjust these paths if the files live somewhere else.
import AddUser from './pages/admin/AddUser';
import UserList from './pages/admin/UserList';
import BankDetails from './pages/admin/BankDetails';
import BankList from './pages/admin/BankList';

// inactivity, independent of the Auth0 access-token lifetime.
const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

// Canonical roles are the backend snake_case values returned by
// GET /api/organizations/ (rbac migrations). The map below normalizes every
// role string to that canonical form so downstream consumers only ever compare
// against one vocabulary. Unknown values resolve to null (no role).
const ROLE_ALIASES = {
  admin: 'admin',
  'Organisation Admin': 'admin',
  treasury: 'treasury',
  Treasurer: 'treasury',
  governing_body: 'governing_body',
  'Governing Body': 'governing_body',
  normal_member: 'normal_member',
  'Normal Member': 'normal_member',
  donor: 'donor',
  Donor: 'donor',
  senior_accounting_clerk: 'senior_accounting_clerk',
  'Senior Clerk': 'senior_accounting_clerk',
  junior_accounting_clerk: 'junior_accounting_clerk',
  'Junior Clerk': 'junior_accounting_clerk',
};

const normalizeRole = (rawRole) => {
  if (!rawRole) return null;
  return ROLE_ALIASES[rawRole] || null;
};

function App() {
  const { isAuthenticated, isLoading, user, logout, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation('common');

  // ── Navigation ──────────────────────────────────────────────────────────
  // Previously this was a single `page` string plus a single `prevDashboard`
  // string. That only tracks ONE level of history, and `prevDashboard` was
  // only ever updated by goTo() — a plain onBack={() => setPage(prevDashboard)}
  // (used by ContributionForm, Contributions, etc.) never updated it. That
  // caused a real bug: going A -> goTo(B) -> plain-back-to-A left
  // prevDashboard still pointing at A (itself), so clicking Back again on A
  // called setPage('A') while already on 'A' — no state change, so nothing
  // visibly happened (e.g. the "back button" after submitting a contribution
  // and returning to Contributions).
  //
  // Fixed by using a real stack: goTo() pushes, goBack() pops, goHome()
  // resets to just ['home']. `page` is always the top of the stack.
  const [pageStack, setPageStack] = useState(['home']);
  const page = pageStack[pageStack.length - 1];

  const [selectedNgo, setSelectedNgo] = useState(null);

  const profileKey = user?.sub ? `profileDone_${user.sub}` : null;
  const [isNewUser, setIsNewUser] = useState(false);
  const [role, setRole] = useState(null);
  const [pendingReauth, setPendingReauth] = useState(false);
  const reauthRan = useRef(false);

  // NOTE: startup connectivity check removed — /api/organizations/ requires auth,
  // so an unauthenticated fetch always returned 403 and polluted the logs.

  // 1. Hooks must ALWAYS go at the top level
  useEffect(() => {
    if (isAuthenticated && user?.sub) {
      const done = localStorage.getItem(`profileDone_${user.sub}`);
      if (!done) setIsNewUser(true);
    }
  }, [isAuthenticated, user]);

  // Remember that a session exists, and tidy the Auth0 callback params out of
  // the URL now that auth0-react has consumed them.
  useEffect(() => {
    if (isAuthenticated) {
      setSessionFlag();
      stripCallbackParams();
    }
  }, [isAuthenticated]);

  // Full-page silent re-auth (authorize?prompt=none) when a reload lands on
  // the Login screen but a session flag says one existed. Top-level
  // navigations carry the Auth0 SSO cookie even when third-party cookies are
  // blocked, so this restores the session without a password or click. If
  // Auth0 has no SSO session it bounces back with error=login_required, which
  // we detect here, clear the flag, and show Login (no redirect loop).
  useEffect(() => {
    if (isLoading || isAuthenticated || pendingReauth || reauthRan.current) return;

    if (hasStaleCallbackParams()) {
      reauthRan.current = true;
      clearSessionFlag();
      stripCallbackParams();
      logger.warn('auth-reload', 'Silent re-auth was not possible (Auth0 returned no session)');
      return;
    }

    if (!hasSessionFlag()) return;

    reauthRan.current = true;
    setPendingReauth(true);
    loginWithRedirect({ authorizationParams: { prompt: 'none' } }).catch((err) => {
      clearSessionFlag();
      stripCallbackParams();
      setPendingReauth(false);
      logger.warn('auth-reload', `Silent re-auth failed: ${err.error}`, err.message);
    });
  }, [isLoading, isAuthenticated, pendingReauth, loginWithRedirect]);

  const handleLogout = (logoutParams) => {
    clearSessionFlag();
    logout(logoutParams);
  };

  // BUG-002 / RM_32: client-side inactivity timeout. Independent of the
  // Auth0 token's own lifetime — this fires purely off DOM activity (mouse,
  // keyboard, touch, scroll), so a session is ended locally even if the
  // access token would otherwise still be valid. Logging out clears the
  // session flag and drops the user back on the Login screen, which is the
  // re-authentication prompt RM_32 calls for.
  useIdleTimeout(IDLE_TIMEOUT_MS, () => {
    if (isAuthenticated) {
      logger.info('auth-idle', `Session ended after ${IDLE_TIMEOUT_MS / 60000} minutes of inactivity`);
      handleLogout({ logoutParams: { returnTo: window.location.origin } });
    }
  });
 
  const handleProfileComplete = () => {
    if (profileKey) localStorage.setItem(profileKey, 'true');
    setIsNewUser(false);
  };

  const getRoleDestination = (roleToCheck) => {
    switch (roleToCheck) {
      case 'admin':                   return 'admin';
      case 'treasury':                return 'treasury';
      case 'senior_accounting_clerk': return 'sr-clerk';
      case 'junior_accounting_clerk': return 'jr-clerk';
      case 'governing_body':          return 'governing-body';
      case 'normal_member':           return 'normal-member';
      default:                        return 'dashboard';
    }
  };

  // Push a new page onto the history stack.
  const goTo = (dest) => {
    setPageStack((stack) => [...stack, dest]);
  };

  // Pop back to the previous page in the stack. No-op if already at the root.
  const goBack = () => {
    setPageStack((stack) => (stack.length > 1 ? stack.slice(0, -1) : stack));
  };

  // Hard reset to home, discarding all history (used by the Home icon and by
  // every "top-level dashboard" page's Back, which has always jumped
  // straight to Home rather than stepping back one level).
  const goHome = () => {
    setPageStack(['home']);
  };

  const handleNgoSelect = (ngo) => {
    setSelectedNgo(ngo);
    setRole(normalizeRole(ngo.role));
    // Selecting an NGO always lands on the general dashboard with a fresh
    // history — "back" from there should go to Home.
    setPageStack(['home', 'dashboard']);
  };

  if (isLoading || pendingReauth) return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-gray-500 text-sm">{t('loading')}</p>
    </div>
  );

  if (!isAuthenticated) return <Login />;
  if (isAuthenticated && isNewUser) return (
    <Profile
      onComplete={handleProfileComplete}
      onCancel={() => handleLogout({ logoutParams: { returnTo: window.location.origin } })}
    />
  );

  if (page === 'contribution-form') return (
    <ContributionForm 
      orgId={selectedNgo?.id} 
      getToken={getAccessTokenSilently} 
      onBack={goBack} 
      onEditProfile={() => goTo('profile')}
    />
  );

  if (page === 'add-expenses') return (
    <AddExpensePage
      orgId={selectedNgo?.id}
      onBack={goBack}
      onHome={goHome}
      onSubmitExpense={goBack}
      onEditProfile={() => goTo('profile')}
    />
  );

  if (page === 'expenses') return (
    <ExpensesPage
      onAddExpense={() => goTo('add-expenses')}
      onBack={goBack}
      onHome={goHome}
      onEditProfile={() => goTo('profile')}
    />
  );
  if (page === 'view-expenses') return (
    <ViewExpenses
      ngo={selectedNgo}
      userRole={role}
      onAddExpense={() => goTo('add-expenses')}
      onBack={goBack}
      onHome={goHome}
      onEditProfile={() => goTo('profile')}
    />
  );
  
  if (page === 'contributions') return (
  <Contributions
    ngo={selectedNgo}
    onAddContribution={() => goTo('contribution-form')}
    onBack={goBack}
    onHome={goHome}
    onEditProfile={() => goTo('profile')}
  />
);

  if (page === 'transactions') return (
    <Transactions
      ngo={selectedNgo}
      onBack={goBack}
      onHome={goHome}
      onEditProfile={() => goTo('profile')}
    />
  );

  if (page === 'approve-expense') return (
    <ApproveExpense
      ngo={selectedNgo}
      userRole={role}
      onBack={goBack}
      onHome={goHome}
      onEditProfile={() => goTo('profile')}
    />
  );

  if (page === 'delete-transaction') return (
    <DeleteTransaction
      userRole={role}
       orgId={selectedNgo?.id}
      getToken={getAccessTokenSilently}
      onBack={goBack}
      onHome={goHome}
      onEditProfile={() => goTo('profile')}
    />
  );

  if (page === 'pending-deletes') return (
    <PendingDeleteRequests
      userRole={role}
      orgId={selectedNgo?.id}
      getToken={getAccessTokenSilently}
      onBack={goBack}
      onHome={goHome}
      onEditProfile={() => goTo('profile')}
    />
  );

  if (page === 'my-deletion-requests') return (
    <MyDeletionRequests
      userRole={role}
      orgId={selectedNgo?.id}
      getToken={getAccessTokenSilently}
      onBack={goBack}
      onHome={goHome}
      onEditProfile={() => goTo('profile')}
    />
  );

  if (page === 'reports') return (
    <Reports
      ngo={selectedNgo}
      userRole={role}
      onBack={goBack}
      onHome={goHome}
      onEditProfile={() => goTo('profile')}
    />
  );

  if (page === 'notifications') return (
    <Notifications
      ngo={selectedNgo}
      userRole={role}
      onBack={goBack}
      onHome={goHome}
      onEditProfile={() => goTo('profile')}
    />
  );

  if (page === 'timeline') return (
    <Timeline
      ngo={selectedNgo}
      userRole={role}
      onBack={goBack}
      onHome={goHome}
      onEditProfile={() => goTo('profile')}
    />
  );

  if (page === 'create-org') return (
    <CreateOrganization
      onBack={goHome}
      onSuccess={goHome}
      onEditProfile={() => goTo('profile')}
    />
  );

  if (page === 'profile') return (
    <Profile
      onComplete={goBack}
      onCancel={goBack}
    />
  );

  // ── NEW: Admin sub-pages ────────────────────────────────────────────────
  // These previously lived inside AdminDashboard's own local `view` state
  // (with a separate, buggy `editingProfile` flag). Now they're ordinary
  // stack entries, exactly like Contributions/Reports/Timeline above, so
  // Back and Edit Profile behave consistently everywhere.
  if (page === 'add-user') return (
    <AddUser
      ngo={selectedNgo}
      onBack={goBack}
      onHome={goHome}
      onEditProfile={() => goTo('profile')}
    />
  );

  if (page === 'user-list') return (
    <UserList
      ngo={selectedNgo}
      onBack={goBack}
      onHome={goHome}
      onEditProfile={() => goTo('profile')}
    />
  );

  if (page === 'add-bank') return (
    <BankDetails
      ngo={selectedNgo}
      onBack={goBack}
      onHome={goHome}
      onEditProfile={() => goTo('profile')}
    />
  );

  if (page === 'bank-list') return (
    <BankList
      ngo={selectedNgo}
      onBack={goBack}
      onHome={goHome}
      onEditProfile={() => goTo('profile')}
    />
  );

  if (page === 'dashboard') return (
    <Dashboard
      ngo={selectedNgo}
      userRole={role}
      onTimeline={() => goTo('timeline')}
      onBack={goHome}
      onHome={goHome}
      onContribution={() => goTo('contribution-form')}
      onTreasury={() => goTo('treasury')}
      onEditProfile={() => goTo('profile')}
      onDashboard={() => {
        const resolvedRole = normalizeRole(selectedNgo?.role);
        const dest = getRoleDestination(resolvedRole);
        if (dest === 'dashboard') {
          goHome();
        } else {
          goTo(dest);
        }
      }}
    />
  );

  if (page === 'treasury') return (
    <TreasuryDashboard
      ngo={selectedNgo}
      onHome={goHome}
      onBack={goHome}
      onEditProfile={() => goTo('profile')}
      onContributions={() => goTo('contributions')}
      onAddExpenses={() => goTo('add-expenses')}
      onViewExpenses={() => goTo('view-expenses')}
      onTransactions={() => goTo('transactions')}
      onApproveExpense={() => goTo('approve-expense')}
      onDeleteRequest={() => goTo('delete-transaction')}
      onPendingDeletes={() => goTo('pending-deletes')}
      onReports={() => goTo('reports')}
      onNotifications={() => goTo('notifications')}
    />
  );

  if (page === 'sr-clerk') return (
    <SrClerkDashboard
      ngo={selectedNgo}
      onHome={goHome}
      onBack={goHome}
      onEditProfile={() => goTo('profile')}
      onContributions={() => goTo('contributions')}
      onAddExpenses={() => goTo('add-expenses')}
      onDeleteTransaction={() => goTo('delete-transaction')}
      onMyDeletionRequests={() => goTo('my-deletion-requests')}
      onTransactions={() => goTo('transactions')}
      onApproveExpense={() => goTo('approve-expense')}
      onReports={() => goTo('reports')}
      onNotifications={() => goTo('notifications')}
    />
  );

  if (page === 'governing-body') return (
  <GoverningBodyDashboard
    ngo={selectedNgo}
    onHome={goHome}
    onBack={goHome}
    onExpenses={() => goTo('view-expenses')}
    onAddExpenses={() => goTo('add-expenses')}
    onTransactions={() => goTo('transactions')}
    onEditProfile={() => goTo('profile')}
  />
);

  if (page === 'admin') return (
    <AdminDashboard
        ngo={selectedNgo}
        onHome={goHome}
        onBack={goHome}
        onAddExpenses={() => goTo('add-expenses')}
        onReports={() => goTo('reports')}
        // NEW: AdminDashboard no longer renders these itself — it just
        // asks App.js to push them onto the pageStack, same as every
        // other dashboard does for its sub-pages.
        onAddUser={() => goTo('add-user')}
        onUserList={() => goTo('user-list')}
        onAddBank={() => goTo('add-bank')}
        onBankList={() => goTo('bank-list')}
        onEditProfile={() => goTo('profile')}
        user={user}
        onLogout={() => handleLogout({ logoutParams: { returnTo: window.location.origin } })}
    />
  );

  if (page === 'jr-clerk') return (
    <JrClerkDashboard
      ngo={selectedNgo}
      onHome={goHome}
      onBack={goHome}
      onEditProfile={() => goTo('profile')}
      onContributions={() => goTo('contributions')}
      onAddExpenses={() => goTo('add-expenses')}
      onNotifications={() => goTo('notifications')}
      onReports={() => goTo('reports')}
    />
  );

  if (page === 'normal-member') return (
    <NormalMemberDashboard
      ngo={selectedNgo}
      onHome={goHome}
      onBack={goHome}
      onEditProfile={() => goTo('profile')}
      onAddExpenses={() => goTo('add-expenses')}
    />
  );

  return (
    <Home
      onSelectNgo={handleNgoSelect}
      onCreateOrg={() => goTo('create-org')}
    />
  );
}

export default App;